/**
 * Mode identifiers for the ruler overlay.
 * Add new entries here and register a corresponding RulerMode
 * implementation in RULER_MODES below.
 */
export type RulerModeId = "default" | "center";

/**
 * A non-interactive guide line rendered by the mode itself
 * (e.g. the center-cross in "center" mode).
 */
export interface FixedGuideLine {
  axis: "horizontal" | "vertical";
  /** Absolute document-coordinate position (px). */
  position: number;
}

/**
 * Strategy interface for ruler modes.
 *
 * Each mode controls:
 *  - how tick-mark numbers are displayed
 *  - what the corner element shows
 *  - whether any permanent helper lines exist
 */
export interface RulerMode {
  readonly id: RulerModeId;

  /** Label shown inside the corner square. */
  readonly cornerLabel: string;

  /**
   * Transforms an absolute document-coordinate into a display value
   * for ruler ticks and guide-line labels.
   *
   * Default mode: identity (returns docPos).
   * Center mode:  returns offset from viewport center.
   */
  formatPosition(
    docPos: number,
    axis: "horizontal" | "vertical",
    viewportSize: number,
    scroll: number,
  ): number;

  /**
   * Returns permanent (non-removable, non-draggable) guide lines
   * for this mode. Called every scroll/resize frame.
   */
  getFixedLines(
    viewportWidth: number,
    viewportHeight: number,
    scrollX: number,
    scrollY: number,
  ): FixedGuideLine[];
}

// ── Concrete modes ──────────────────────────────────────

class DefaultMode implements RulerMode {
  readonly id = "default" as const;
  readonly cornerLabel = "↗";

  formatPosition(docPos: number): number {
    return docPos;
  }

  getFixedLines(): FixedGuideLine[] {
    return [];
  }
}

class CenterMode implements RulerMode {
  readonly id = "center" as const;
  readonly cornerLabel = "+";

  formatPosition(
    docPos: number,
    axis: "horizontal" | "vertical",
    viewportSize: number,
    scroll: number,
  ): number {
    const center = scroll + viewportSize / 2;
    return docPos - center;
  }

  getFixedLines(
    viewportWidth: number,
    viewportHeight: number,
    scrollX: number,
    scrollY: number,
  ): FixedGuideLine[] {
    return [
      { axis: "horizontal", position: scrollY + viewportHeight / 2 },
      { axis: "vertical",   position: scrollX + viewportWidth  / 2 },
    ];
  }
}

// ── Registry ────────────────────────────────────────────

export const RULER_MODES: Record<RulerModeId, RulerMode> = {
  default: new DefaultMode(),
  center:  new CenterMode(),
};

/** Cycling order for corner-click. */
export const MODE_ORDER: RulerModeId[] = ["default", "center"];
